import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fmtINR, downloadCsv } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { PageHeader } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import Payslip from "@/components/Payslip";
import { Loader2, Play, Printer, Eye, Wallet, Send, Download } from "lucide-react";
import { toast } from "sonner";

export default function Payroll() {
  const { role } = useOutletContext();
  const qc = useQueryClient();
  const isHR = role === "hr";
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [view, setView] = useState(null);
  const [autoEmail, setAutoEmail] = useState(false);

  const { data: payslips = [], isLoading } = useQuery({
    queryKey: ["payroll"], queryFn: async () => (await api.get("/payroll")).data,
  });

  const processMut = useMutation({
    mutationFn: async () => (await api.post(`/payroll/process?month=${month}&email=${autoEmail}`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["payroll"] }); toast.success(`Processed ${d.processed} payslip(s) for ${month}${autoEmail ? ` — emailed ${d.emailed}, failed ${d.email_failed}` : ""}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const emailMut = useMutation({
    mutationFn: async (id) => (await api.post(`/payroll/${id}/email`)).data,
    onSuccess: (d) => toast.success(`Payslip emailed to ${d.to}`),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const months = [...new Set(payslips.map((p) => p.month))].sort().reverse();
  const totalNet = payslips.reduce((s, p) => s + p.net, 0);

  return (
    <div>
      <PageHeader title="Payroll & Statutory CTC" subtitle={isHR ? "Process monthly payroll and issue payslips." : "Your monthly payslips and CTC."}>
        {isHR && (
          <div className="flex flex-wrap items-center gap-3">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-10" data-testid="payroll-month-input" />
            <Button onClick={() => processMut.mutate()} disabled={processMut.isPending} data-testid="payroll-process-button" className="rounded-lg font-semibold active:scale-[0.98]">
              {processMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" /> Run Payroll</>}
            </Button>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <Checkbox checked={autoEmail} onCheckedChange={(v) => setAutoEmail(!!v)} data-testid="payroll-auto-email-checkbox" />
              Email payslips to employees
            </label>
            <Button variant="outline" size="sm" className="rounded-lg" onClick={() => downloadCsv("/export/payroll.csv", "skanovix_payroll.csv")} data-testid="payroll-export-button">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border border-border rounded-xl"><p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Payslips</p><p className="font-heading text-2xl font-extrabold mt-1">{payslips.length}</p></Card>
        <Card className="p-4 border border-border rounded-xl"><p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Payroll Months</p><p className="font-heading text-2xl font-extrabold mt-1">{months.length}</p></Card>
        <Card className="p-4 border border-border rounded-xl col-span-2"><p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Total Net Disbursed</p><p className="font-heading text-2xl font-extrabold mt-1 text-accent">{fmtINR(totalNet)}</p></Card>
      </div>

      <Card className="border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Wallet className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Payslip Register</h3></div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>{isHR && <TableHead>Employee</TableHead>}
                  <TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payslips yet.{isHR && " Run payroll to generate."}</TableCell></TableRow>}
                {payslips.map((p) => (
                  <TableRow key={p.id} data-testid={`payslip-row-${p.id}`}>
                    <TableCell className="font-mono text-xs">{new Date(p.month + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" })}{p.email_bounced && <span className="ml-1 text-[10px] font-medium text-destructive" data-testid={`payslip-bounced-${p.id}`} title="Payslip email bounced — resend it">• bounced</span>}</TableCell>
                    {isHR && <TableCell className="font-medium">{p.employee_name}</TableCell>}
                    <TableCell className="text-right font-mono">{fmtINR(p.gross)}</TableCell>
                    <TableCell className="text-right font-mono text-destructive">-{fmtINR(p.total_deductions)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-accent">{fmtINR(p.net)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setView(p)} data-testid={`view-payslip-${p.id}`}><Eye className="w-4 h-4" /></Button>
                      {isHR && <Button size="sm" variant="ghost" onClick={() => emailMut.mutate(p.id)} disabled={emailMut.isPending} data-testid={`email-payslip-${p.id}`} title="Email payslip to employee"><Send className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading flex items-center justify-between">
              Payslip
              <Button size="sm" variant="outline" className="rounded-lg mr-4" onClick={() => window.print()} data-testid="download-payslip-button">
                <Printer className="w-4 h-4 mr-1.5" /> Print / Download PDF
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">Monthly payslip with earnings and statutory deductions.</DialogDescription>
          </DialogHeader>
          {view && <Payslip slip={view} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
